import { useLanguage } from '@/i18n/LanguageProvider';
import { getFitnessWorkoutExperienceCopy } from '@/lib/fitnessWorkoutExperienceCopy';
import type { getFitnessDraftPersistenceStatus } from '@/lib/fitnessDraftPersistence';

type DraftSnapshot = Pick<ReturnType<typeof getFitnessDraftPersistenceStatus>, 'status' | 'operation'>;

export default function WorkoutDraftPersistenceStatus({
  snapshot, onRetry, busy = false, className = '',
}: {
  snapshot: DraftSnapshot;
  onRetry: () => void;
  busy?: boolean;
  className?: string;
}) {
  const { lang, t } = useLanguage();
  const copy = getFitnessWorkoutExperienceCopy(lang);
  if (snapshot.status === 'idle' || (snapshot.status === 'saved' && snapshot.operation !== 'write' && snapshot.operation !== 'remove')) return null;
  const failed = snapshot.status === 'failed';
  const removing = snapshot.operation === 'remove';
  const label = snapshot.status === 'hydrating' ? copy.draftHydrating
    : failed ? (removing ? copy.draftRemoveFailed : snapshot.operation === 'hydrate' ? copy.draftHydrateFailed : copy.draftFailed)
    : snapshot.status === 'saved' ? (removing ? copy.draftRemoved : copy.draftSaved)
    : removing ? copy.draftRemoving : copy.draftPending;
  return <div role={failed ? 'alert' : 'status'} aria-busy={busy || snapshot.status === 'hydrating' || snapshot.status === 'pending'}
    data-fitness-draft-status={snapshot.status}
    className={`flex flex-wrap items-center justify-between gap-x-3 gap-y-1 rounded-xl border px-3 py-2 text-xs leading-relaxed ${failed ? 'border-destructive/40 bg-card text-foreground' : 'border-border bg-card/80 text-muted-foreground'} ${className}`}>
    <p className="min-w-0 flex-1">{label}</p>
    {failed && <button type="button" onClick={onRetry} disabled={busy}
      className="min-h-11 shrink-0 rounded-lg px-3 font-medium text-primary underline focus-visible:ring-2 focus-visible:ring-primary disabled:opacity-50">
      {t('common.retry')}
    </button>}
  </div>;
}
